import { http } from "../http";

export const getPatients = ({ queryKey }: any) => {
  return http.get(`/clinicians/${queryKey[1]}/patients`, {
    params: {
      ...queryKey[2],
    },
  });
};

export const getPatientById = ({ queryKey }: any) => {
  return http.get(`/patients/patient-profile/${queryKey[1]}`);
};

export const getClinicianProfile = () => {
  return http.get("/clinicians/profile");
};

export const getSessions = ({ queryKey }: any) => {
  return http.get("/sessions", {
    params: {
      ...queryKey[1],
    },
  });
};

export const getSessionById = ({ queryKey }: any) => {
  return http.get(`/sessions/${queryKey[1]}`);
};

export const joinSession = ({ queryKey }: any) => {
  return http.get(`/sessions/join`, {
    params: {
      ...queryKey[1],
    },
  });
};

export const getDashboardData = ({ queryKey }: any) => {
  return http.get(`/dashboard/clinicians/${queryKey[1]}`);
};

export const getSessionData = ({ queryKey }: any) => {
  return http.get(`/dashboard/clinicians/${queryKey[1]}/my-sessions`);
};

export const getClinicianTimeSlots = async ({ queryKey }: any) => {
  return await http.get(`clinician-time-slot/clinicians/${queryKey[1]}/slots`);
};

export const updateTimeSlots = (body: any) => {
  return http.patch("/clinician-time-slot", body);
};

// ============================================
// Enhanced Time Slot Management
// ============================================

/**
 * Create new time slot
 */
export const createTimeSlot = (body: any) => {
  return http.post("/clinician-time-slot", body);
};

/**
 * Delete time slot
 */
export const deleteTimeSlot = (id: number) => {
  return http.delete(`/clinician-time-slot/${id}`);
};

/**
 * Get available time slots for a clinician on a date
 */
export const getAvailableTimeSlots = ({ queryKey }: any) => {
  const [, clinicianId, date] = queryKey;
  return http.get(`/clinician-time-slot/clinicians/${clinicianId}/available-slots`, {
    params: { date }
  });
};

/**
 * Get rolling availability for next 7 days
 */
export const getRollingAvailability = ({ queryKey }: any) => {
  const [, clinicianId, startDate] = queryKey;
  return http.get(`/clinician-time-slot/clinicians/${clinicianId}/rolling-availability`, {
    params: { startDate }
  });
};

export const getReports = ({ queryKey }: any) => {
  return http.get(`/reports`, {
    params: {
      ...queryKey[1],
    },
  });
};

export const getReportsById = ({ queryKey }: any) => {
  return http.get(`/reports/${queryKey[1]}`);
};

// ============================================
// Enhanced Reports Management
// ============================================

/**
 * Create or update report (POST to /reports/:id)
 */
export const createOrUpdateReport = ({ id, body }: any) => {
  return http.post(`/reports/${id}`, body);
};

/**
 * Update report (PATCH to /reports/:id)
 */
export const updateReport = ({ id, body }: any) => {
  return http.patch(`/reports/${id}`, body);
};

/**
 * Get reports for a specific session
 */
export const getReportsBySession = ({ queryKey }: any) => {
  return http.get(`/reports/session/${queryKey[1]}`);
};

/**
 * Delete report
 */
export const deleteReport = (id: number) => {
  return http.delete(`/reports/${id}`);
};

/**
 * Get patient's medical history (all reports from previous sessions)
 */
export const getPatientHistory = ({ queryKey }: any) => {
  const patientProfileId = queryKey[1];
  return http.get(`/reports/patient-history/${patientProfileId}`);
};

export const getMedicalRecords = ({ queryKey }: any) => {
  return http.get(`/medical-records`, {
    params: {
      ...queryKey[1],
    },
  });
};

// ============================================
// Enhanced Medical Records Management
// ============================================

/**
 * Upload medical record file
 */
export const uploadMedicalRecord = (body: FormData) => {
  return http.post("/medical-records/upload", body, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Save medical record
 */
export const saveMedicalRecord = (body: any) => {
  return http.post("/medical-records/save", body);
};

/**
 * Update medical record
 */
export const updateMedicalRecord = ({ id, body }: any) => {
  return http.patch(`/medical-records/${id}`, body);
};

/**
 * Delete medical record
 */
export const deleteMedicalRecord = (id: number) => {
  return http.delete(`/medical-records/${id}`);
};

/**
 * Download medical record file
 */
export const downloadMedicalRecord = (id: number) => {
  return http.get(`/medical-records/download/${id}`, {
    responseType: 'blob'
  });
};

export const getClinicianPayouts = ({ queryKey }: any) => {
  return http.get("/payouts", {
    params: {
      ...queryKey[1],
    },
  });
};

export const getPayoutStatistics = ({ queryKey }: any) => {
  return http.get(`/payouts/clinicians/${queryKey[1]}`);
};

// ============================================
// Enhanced Payout Management
// ============================================

/**
 * Create payout record
 */
export const createPayout = (body: any) => {
  return http.post("/payouts", body);
};

/**
 * Get payout by ID
 */
export const getPayoutById = ({ queryKey }: any) => {
  return http.get(`/payouts/${queryKey[1]}`);
};

/**
 * Update payout
 */
export const updatePayout = ({ id, body }: any) => {
  return http.patch(`/payouts/${id}`, body);
};

/**
 * Get overall payout statistics
 */
export const getAllPayoutStatistics = () => {
  return http.get("/payouts/statistics");
};

/**
 * Get payment analytics (admin)
 */
export const getPaymentAnalytics = ({ queryKey }: any) => {
  return http.get("/payouts/admin/payment-analytics", {
    params: {
      ...queryKey[1],
    },
  });
};

/**
 * Create automatic payout for session
 */
export const createAutomaticPayout = (sessionId: number) => {
  return http.post(`/sessions/payouts/create-automatic/${sessionId}`);
};

/**
 * Process individual payout
 */
export const processPayout = (payoutId: number) => {
  return http.post(`/sessions/payouts/process/${payoutId}`);
};

/**
 * Get payout status from PayTabs
 */
export const getPayoutStatus = (transactionRef: string) => {
  return http.get(`/sessions/payouts/status/${transactionRef}`);
};

export const updateClinicianProfile = ({ body, id }: any) => {
  return http.patch(`/clinicians/profile/${id}`, body);
};

export const updateClinicianSession = ({ body, id }: any) => {
  return http.patch(`/sessions/update-session-clinician/${id}`, body);
};

export const getSessionPackage = () => {
  return http.get(`/mode-of-consultation`);
};

export const generateToken = (body: any) => {
  return http.post("/sessions/token", body);
};

export function getAllResources({ queryKey }: any) {
  return http.get(`/resources/`, {
    params: {
      ...queryKey[1],
    },
  });
}

export function getAllServices({ queryKey }: any) {
  return http.get(`/services/`, {
    params: {
      ...queryKey[1],
    },
  });
}

export const updateSession = ({ id, body }: any) => {
  return http.patch(`sessions/update-session-status/${id}`, body);
};

export const generateReport = (body: any) => {
  return http.post(`reports`, body);
};

export const postDuration = (body: any) => {
  return http.post(`duration`, body);
};

// ============================================
// Notifications
// ============================================

/**
 * Register push notification token
 */
export const registerNotificationToken = (body: any) => {
  return http.post("/notifications/tokens", body);
};

/**
 * Unregister push notification token
 */
export const unregisterNotificationToken = (token: string) => {
  return http.delete(`/notifications/tokens/${token}`);
};

/**
 * Get notification preferences
 */
export const getNotificationPreferences = () => {
  return http.get("/notifications/preferences");
};

/**
 * Update notification preferences
 */
export const updateNotificationPreferences = (body: any) => {
  return http.patch("/notifications/preferences", body);
};

// Google Meet Integration API Functions
export const createGoogleMeetLink = (body: any) => {
  return http.post("/sessions/create-meet", body);
};

export const updateSessionWithMeet = (sessionId: string, body: any) => {
  return http.patch(`/sessions/${sessionId}/google-meet`, body);
};

export const getSessionDetails = ({ queryKey }: any) => {
  const sessionId = queryKey[1];
  return http.get(`/sessions/${sessionId}`);
};

export const getSessionsList = ({ queryKey }: any) => {
  const params = queryKey[1] || {};
  return http.get("/sessions", {
    params: {
      ...params,
    },
  });
};

// ============================================
// Additional Session Management
// ============================================

/**
 * Check if time slots are available for booking
 */
export const checkSlotAvailability = (body: any) => {
  return http.post("/sessions/check-slot-availability", body);
};

/**
 * Verify session OTP
 */
export const verifySessionOtp = ({ sessionId, otp }: any) => {
  return http.get(`/sessions/${sessionId}/verify-otp`, {
    params: { otp }
  });
};

/**
 * Generate PDF report for session
 */
export const generateSessionPdfReport = (sessionId: number) => {
  return http.get(`/sessions/${sessionId}/generate-report`);
};

/**
 * Report an issue with a session
 */
export const reportSessionIssue = ({ sessionId, body }: any) => {
  return http.post(`/sessions/${sessionId}/report-issue`, body);
};

/**
 * Get support tickets for clinician's sessions
 */
export const getMySessionTickets = () => {
  return http.get("/sessions/my-tickets");
};

/**
 * Change/assign new clinician to session
 */
export const assignNewClinician = ({ id, body }: any) => {
  return http.patch(`/sessions/assign-new-clinician/${id}`, body);
};

/**
 * Get session statistics
 */
export const getSessionStatistics = () => {
  return http.get("/sessions/statistics");
};

/**
 * Get available clinicians for booking
 */
export const getAvailableClinicians = ({ queryKey }: any) => {
  return http.get("/sessions/available-clinicians", {
    params: {
      ...queryKey[1],
    },
  });
};

/**
 * Cancel session by clinician
 */
export const cancelSessionByClinician = ({ id, body }: any) => {
  return http.patch(`/sessions/cancel-session-clinician/${id}`, body);
};

// ═══════════════════════════════════════════════════════════════════════════
// VOICE EXERCISES & AI ASSISTANT
// ═══════════════════════════════════════════════════════════════════════════

/** Get available AI models */
export const getVoiceExerciseModels = ({ queryKey }: any) => {
  return http.get("/voice-exercises/models", {
    params: { category: queryKey[1] },
  });
};

/** Get suggested AI models for a diagnosis */
export const getSuggestedModels = ({ queryKey }: any) => {
  return http.get("/voice-exercises/models/suggest", {
    params: { diagnosisCode: queryKey[1] },
  });
};

/** Create assignment (assign AI models to patient) */
export const createVoiceAssignment = (body: any) => {
  return http.post("/voice-exercises/assignments", body);
};

/** Get assignments for a patient profile */
export const getPatientAssignments = ({ queryKey }: any) => {
  return http.get("/voice-exercises/assignments", {
    params: { patientProfileId: queryKey[1], status: queryKey[2] },
  });
};

/** Update an assignment */
export const updateVoiceAssignment = ({ id, body }: any) => {
  return http.patch(`/voice-exercises/assignments/${id}`, body);
};

/** Delete an assignment */
export const deleteVoiceAssignment = (id: number) => {
  return http.delete(`/voice-exercises/assignments/${id}`);
};

/** Get patient exercises (clinician view) */
export const getPatientExercises = ({ queryKey }: any) => {
  const [, patientProfileId, limit, offset] = queryKey;
  return http.get(`/voice-exercises/patients/${patientProfileId}/exercises`, {
    params: { limit: limit || 50, offset: offset || 0 },
  });
};

/** Get all AI analyses for a patient (clinician view) */
export const getPatientAiAnalyses = ({ queryKey }: any) => {
  return http.get(`/voice-exercises/patients/${queryKey[1]}/ai-analysis`);
};

/** Submit clinician review of AI analysis */
export const submitAiReview = ({ analysisId, body }: any) => {
  return http.post(`/voice-exercises/ai-analysis/${analysisId}/review`, body);
};

/** Get clinical impressions */
export const getClinicalImpressions = ({ queryKey }: any) => {
  return http.get("/voice-exercises/impressions", {
    params: { category: queryKey[1] },
  });
};

/** Get patient exercise progress */
export const getPatientExerciseProgress = ({ queryKey }: any) => {
  return http.get("/voice-exercises/progress", {
    params: { patientProfileId: queryKey[1] },
  });
};

/** Get patient progress timeline */
export const getPatientProgressTimeline = ({ queryKey }: any) => {
  return http.get("/voice-exercises/progress/timeline", {
    params: { patientProfileId: queryKey[1], days: queryKey[2] || 30 },
  });
};

/** Get patient voice exercise session summary for clinician review */
export const getPatientSessionSummary = ({ queryKey }: any) => {
  return http.get(`/voice-exercises/patients/${queryKey[1]}/session-summary`, {
    params: queryKey[2] ? { since: queryKey[2] } : {},
  });
};
